import { assign, EventObject, sendParent, StateNodeConfig, StateSchema } from 'xstate';
import { QueryParamKeys } from '../constants/query-params';
import { BadRequestReasons, Details, DetailsInput } from '../gql/graphql';
import { buildAnalytics } from '../helpers/analytics/buildAnalytics';
import { getNewOrderDetailsFromOrderDetails } from '../helpers/converters/getNewOrderDetailsFromOrderDetails';
import createOrder from '../networkRequests/order/createOrder';
import { PropagateNetworkErrors } from './actions/genericActions';
import { track } from './actions/segment';
import { EcommMachineEvents } from './types/ecommMachine.types';
import { CellPhoneMachineContext } from './types/shared/context/cellPhoneMachineContext';
import { OrderIdMachineContext } from './types/shared/context/orderIdMachineContext';
import { OrderMachineContext } from './types/shared/context/orderMachineContext';

type CreatingNewOrderContext = OrderIdMachineContext & OrderMachineContext & CellPhoneMachineContext;

// apply and createDraftApp alias BadRequest's reason to badRequestReason; it can't share a selection with LeaseRestricted's reason enum
export const isOrderInUseError = (errors?: object[] | null) =>
  !!errors?.some((err) => {
    const extensions = 'extensions' in err ? (err.extensions as { reason?: unknown; badRequestReason?: unknown } | null) : null;
    return extensions?.reason === BadRequestReasons.OrderInUse || extensions?.badRequestReason === BadRequestReasons.OrderInUse;
  });

// AES only carries OTP verification onto the new order when this phone string-equals the token's
const phoneOnlyDetails = (cellPhone: string): DetailsInput | undefined => (cellPhone ? { customer: { cellPhone } } : undefined);

const makeStateCreatingNewOrder = <TContext extends CreatingNewOrderContext, TStateSchema extends StateSchema, TEvent extends EventObject>(
  retryTarget: string,
  trackEventName: string,
  carryOrderDetails: boolean,
): StateNodeConfig<TContext, TStateSchema, TEvent> => {
  return {
    tags: 'calling',
    invoke: {
      src: (context: TContext) =>
        createOrder({
          analytics: buildAnalytics(),
          details: carryOrderDetails ? getNewOrderDetailsFromOrderDetails(context.orderDetails, context.cellPhone) : phoneOnlyDetails(context.cellPhone),
        }),
      onDone: {
        target: retryTarget,
        actions: [
          assign((context: any, event: any) => ({
            hasRetriedWithNewOrder: true,
            orderId: event.data?.data?.createOrder?.order?.id,
            orderDetails: carryOrderDetails ? context.orderDetails : ({} as Details),
          })),
          (context: TContext) => {
            if (!context.orderId) {
              return;
            }
            const url = new URL(window.location.href);
            url.searchParams.set(QueryParamKeys.OrderId, context.orderId);
            url.searchParams.delete(QueryParamKeys.AppStatus);
            window.history.replaceState({}, '', url.toString());
          },
          sendParent((context: TContext) => ({
            type: EcommMachineEvents.ORDER_REPLACED,
            data: { orderId: context.orderId, orderDetails: context.orderDetails },
          })),
          track(trackEventName),
        ] as any,
      },
      onError: { actions: PropagateNetworkErrors },
    },
  };
};

// OR-3290: the session moves to this new order, so it has to keep the cart and customer details the
// integration put on the original one or a Shop First checkout turns into an empty-cart (Apply First) pre-approval.
export const MakeStateCreatingNewOrderForPhoneMismatch = <
  TContext extends CreatingNewOrderContext,
  TStateSchema extends StateSchema,
  TEvent extends EventObject,
>(
  retryTarget: string,
) => makeStateCreatingNewOrder<TContext, TStateSchema, TEvent>(retryTarget, 'New Order Created - Session Associated With Different Phone Number', true);

// OR-3367: the in-use order's cart and customer belong to the application already on it, so none of it moves over.
export const MakeStateCreatingNewOrderForOrderInUse = <TContext extends CreatingNewOrderContext, TStateSchema extends StateSchema, TEvent extends EventObject>(
  retryTarget: string,
) => makeStateCreatingNewOrder<TContext, TStateSchema, TEvent>(retryTarget, 'New Order Created - Order In Use', false);
